import { useState, useRef } from "react";
import { Loader2, Upload, Printer, FileText, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { openBlob } from "@/lib/utils";

async function generatePdf(body: any) {
  const res = await fetch("/api/skugen", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: "Greška pri generisanju PDF-a" }));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  const blob = await res.blob();
  openBlob(blob, "sku-stikeri.pdf");
}

export function SkuGenPage() {
  const [widthMm, setWidthMm] = useState("48");
  const [heightMm, setHeightMm] = useState("44");
  const [codeType, setCodeType] = useState<"qr" | "barcode">("qr");

  return (
    <div className="p-4 flex flex-col gap-4 max-w-md">
      <h2 className="text-sm font-medium">SKU Stikeri</h2>

      <div className="flex gap-3">
        <div className="flex-1">
          <label className="text-xs text-muted-foreground mb-1 block">Širina (mm)</label>
          <Input type="number" value={widthMm} onChange={(e) => setWidthMm(e.target.value)} min={20} max={200} />
        </div>
        <div className="flex-1">
          <label className="text-xs text-muted-foreground mb-1 block">Visina (mm)</label>
          <Input type="number" value={heightMm} onChange={(e) => setHeightMm(e.target.value)} min={20} max={200} />
        </div>
      </div>

      <div>
        <label className="text-xs text-muted-foreground mb-1 block">Tip koda</label>
        <Select value={codeType} onValueChange={(v) => setCodeType(v as "qr" | "barcode")}>
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="qr">QR kod</SelectItem>
            <SelectItem value="barcode">Barkod</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Tabs defaultValue="single">
        <TabsList className="w-full">
          <TabsTrigger value="single" className="flex-1">Jedan stiker</TabsTrigger>
          <TabsTrigger value="csv" className="flex-1">CSV import</TabsTrigger>
        </TabsList>

        <TabsContent value="single" className="mt-3">
          <SingleStickerForm widthMm={widthMm} heightMm={heightMm} codeType={codeType} />
        </TabsContent>

        <TabsContent value="csv" className="mt-3">
          <CsvImportForm widthMm={widthMm} heightMm={heightMm} codeType={codeType} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

type FormProps = { widthMm: string; heightMm: string; codeType: "qr" | "barcode" };

function SingleStickerForm({ widthMm, heightMm, codeType }: FormProps) {
  const [name, setName] = useState("");
  const [sku, setSku] = useState("");
  const [stock, setStock] = useState("1");
  const [declaration, setDeclaration] = useState("");
  const [generating, setGenerating] = useState(false);

  async function handleGenerate() {
    if (!sku.trim()) {
      toast.error("SKU je obavezan");
      return;
    }
    setGenerating(true);
    try {
      await generatePdf({
        items: [{
          name: name.trim(),
          sku: sku.trim(),
          stock: Math.max(1, parseInt(stock) || 1),
          declaration: declaration.trim() || undefined,
        }],
        widthMm: Number(widthMm) || undefined,
        heightMm: Number(heightMm) || undefined,
        codeType,
      });
    } catch (e: any) {
      toast.error(e?.message || "Greška pri generisanju PDF-a");
    } finally {
      setGenerating(false);
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div>
        <label className="text-xs text-muted-foreground mb-1 block">Naziv proizvoda</label>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Majica XL Crna" />
      </div>
      <div>
        <label className="text-xs text-muted-foreground mb-1 block">SKU *</label>
        <Input value={sku} onChange={(e) => setSku(e.target.value)} placeholder="LP000042" />
      </div>
      <div>
        <label className="text-xs text-muted-foreground mb-1 block">Količina stikera</label>
        <Input type="number" value={stock} onChange={(e) => setStock(e.target.value)} min={1} max={500} />
      </div>
      <div>
        <label className="text-xs text-muted-foreground mb-1 block">Deklaracija</label>
        <Input value={declaration} onChange={(e) => setDeclaration(e.target.value)} placeholder="Sastav: 100% pamuk" />
      </div>
      <Button onClick={handleGenerate} disabled={generating || !sku.trim()} className="gap-1.5">
        {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Printer className="h-3.5 w-3.5" />}
        Generiši ({Math.max(1, parseInt(stock) || 1)})
      </Button>
    </div>
  );
}

function CsvImportForm({ widthMm, heightMm, codeType }: FormProps) {
  const [file, setFile] = useState<File | null>(null);
  const [generating, setGenerating] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  async function handleGenerate() {
    if (!file) {
      toast.error("Odaberi CSV fajl");
      return;
    }
    setGenerating(true);
    try {
      const form = new FormData();
      form.append("file", file);
      if (widthMm) form.append("widthMm", widthMm);
      if (heightMm) form.append("heightMm", heightMm);
      form.append("codeType", codeType);

      const res = await fetch("/api/skugen", { method: "POST", body: form });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: "Greška pri generisanju PDF-a" }));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      openBlob(blob, "sku-stikeri.pdf");
    } catch (e: any) {
      toast.error(e?.message || "Greška pri generisanju PDF-a");
    } finally {
      setGenerating(false);
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs text-muted-foreground">
        CSV sa kolonama: Name, SKU, Stock, DEKLARACIJA
      </p>
      <div
        className="border-2 border-dashed rounded-md p-6 flex flex-col items-center gap-2 cursor-pointer hover:border-foreground/30 transition-colors"
        onClick={() => inputRef.current?.click()}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        />
        {file ? (
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-muted-foreground" />
            <span className="text-xs font-medium">{file.name}</span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setFile(null);
                if (inputRef.current) inputRef.current.value = "";
              }}
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : (
          <>
            <Upload className="h-5 w-5 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">Klikni za odabir CSV fajla</span>
          </>
        )}
      </div>
      <Button onClick={handleGenerate} disabled={generating || !file} className="gap-1.5">
        {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Printer className="h-3.5 w-3.5" />}
        Generiši PDF
      </Button>
    </div>
  );
}
